import { type StoragePort } from './types';

/**
 * Porta sobre um Web Storage real. Resolve o storage a cada chamada, então o
 * módulo pode ser importado no servidor: sem `window`, toda leitura é `null` e
 * toda escrita é no-op. Acesso bloqueado pelo navegador vira leitura vazia.
 */
export const createWebStoragePort = (
	resolve: () => Storage | undefined,
): StoragePort => {
	const storage = () => {
		if (typeof window === 'undefined') return undefined;
		try {
			return resolve();
		} catch {
			return undefined;
		}
	};

	return {
		getItem: (name) => {
			try {
				return storage()?.getItem(name) ?? null;
			} catch {
				return null;
			}
		},
		setItem: (name, raw) => {
			storage()?.setItem(name, raw);
		},
		removeItem: (name) => {
			try {
				storage()?.removeItem(name);
			} catch {}
		},
		names: () => {
			const target = storage();
			if (!target) return [];
			const names: string[] = [];
			for (let index = 0; index < target.length; index += 1) {
				const name = target.key(index);
				if (name !== null) names.push(name);
			}
			return names;
		},
	};
};

/** Evento `storage` do navegador: só dispara para mudanças feitas por outra aba. */
export const onWindowStorageChange = (
	listener: (name: string | null) => void,
) => {
	if (typeof window === 'undefined') return () => {};
	const handler = (event: StorageEvent) => listener(event.key);
	window.addEventListener('storage', handler);
	return () => window.removeEventListener('storage', handler);
};
